import { useMemo, type ReactNode } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  Inbox,
  Minus,
  Sparkles,
  Ticket,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { KPICard } from '@/components/charts/KPICard';
import { AnimatedNumber } from '@/components/charts/AnimatedNumber';
import { useCouponPerformance, useReferralPerformance, useUserCohorts, useUserFunnel } from '@/api/bi';
import { compareRangeFor, useFilters, rangeISO } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Growth — users & retention.
 *
 * Stage 1 polish:
 *   - All headline metrics animate via `<AnimatedNumber>` for delta perception.
 *   - Deltas hide when the user has compare mode "Off", per global preference.
 *   - Funnel rows render as a horizontal "ratio bar" so drop-off is glanceable.
 *   - Cohort heat-map gets a tiny legend so colours read consistently.
 *   - EmptyState gets a CTA + tip when the cohorts query yields nothing.
 */
export function GrowthPage() {
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const compareMode = useFilters((s) => s.compareMode);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const compareRange = useMemo(
    () => compareRangeFor(range, compareMode, timeRangeMode),
    [range, compareMode, timeRangeMode],
  );
  const filters = {
    start: iso.start,
    end: iso.end,
    compareStart: compareRange?.start,
    compareEnd: compareRange?.end,
  };
  const promoFilters = { ...filters, storeIds };

  const funnel = useUserFunnel(filters);
  const cohorts = useUserCohorts(filters);
  const coupons = useCouponPerformance({ ...promoFilters, limit: 5 });
  const referrals = useReferralPerformance({ ...promoFilters, limit: 5 });

  const cur = funnel.data?.current;
  const deltas = funnel.data?.deltas ?? {};
  const compareEnabled = compareMode !== 'none';

  const funnelRows = useMemo(() => {
    if (!cur) return [];
    const top = Math.max(1, cur.verified_users || cur.active_users || 1);
    return [
      { label: 'Verified', cur: cur.verified_users, prev: funnel.data?.previous.verified_users },
      { label: 'Active', cur: cur.active_users, prev: funnel.data?.previous.active_users },
      { label: 'Has cart', cur: cur.users_with_cart_products, prev: funnel.data?.previous.users_with_cart_products },
      { label: 'Converted', cur: cur.converted_users, prev: funnel.data?.previous.converted_users },
    ].map((r) => ({ ...r, ratio: r.cur / top }));
  }, [cur, funnel.data]);

  return (
    <>
      <PageHeader
        eyebrow="Growth"
        title="Users & Retention"
        description="Acquisition, conversion, and how cohorts behave week over week."
      />

      <div className="grid gap-4 stagger sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Total users"
          value={
            <AnimatedNumber value={cur?.total_users ?? null} format={(n) => fmt.compact(n)} duration={500} />
          }
          loading={funnel.isLoading}
          hint="All users in the system (not range-scoped)."
        />
        <KPICard
          label="Active in range"
          value={
            <AnimatedNumber value={cur?.active_users ?? null} format={(n) => fmt.compact(n)} duration={500} />
          }
          deltaPct={compareEnabled ? (deltas['active_users_pct'] as number | null | undefined) ?? null : null}
          loading={funnel.isLoading}
          hint="Users with last-active timestamp inside the selected range."
        />
        <KPICard
          label="Converted"
          value={
            <AnimatedNumber value={cur?.converted_users ?? null} format={(n) => fmt.number(n)} duration={500} />
          }
          deltaPct={compareEnabled ? (deltas['converted_users_pct'] as number | null | undefined) ?? null : null}
          loading={funnel.isLoading}
          hint="Distinct users who placed at least one order in range."
        />
        <KPICard
          label="Conversion rate"
          value={
            <AnimatedNumber value={cur?.conversion_rate ?? null} format={(n) => fmt.pct(n)} duration={500} />
          }
          deltaPct={compareEnabled ? (deltas['conversion_rate_pct'] as number | null | undefined) ?? null : null}
          loading={funnel.isLoading}
          hint="Converted ÷ Active."
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Funnel</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardBody>
            {funnel.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : funnel.isError ? (
              <ErrorState onRetry={() => void funnel.refetch()} />
            ) : !cur ? (
              <EmptyState
                icon={<Inbox className="h-5 w-5" />}
                title="No funnel data"
                description="The selected range has no user activity recorded."
                withTip
              />
            ) : (
              <ul className="space-y-2 text-sm">
                {funnelRows.map((r, i) => (
                  <FunnelRow key={r.label} {...r} compareEnabled={compareEnabled} index={i} />
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Weekly retention</CardTitle>
            <div className="flex items-center gap-2">
              {cohorts.data ? (
                <Badge tone="neutral">{cohorts.data.cohorts.length} cohorts</Badge>
              ) : null}
              <RetentionLegend />
            </div>
          </CardHeader>
          <CardBody className="!pt-0">
            {cohorts.isLoading ? (
              <Skeleton className="h-56 w-full" />
            ) : cohorts.isError ? (
              <ErrorState onRetry={() => void cohorts.refetch()} />
            ) : (cohorts.data?.cohorts.length ?? 0) === 0 ? (
              <EmptyState
                icon={<Inbox className="h-5 w-5" />}
                title="Not enough data for cohorts"
                description="Try widening the date range to include at least four full weeks."
                withTip
              />
            ) : (
              <CohortMatrix cohorts={cohorts.data?.cohorts ?? []} />
            )}
          </CardBody>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Coupons & referrals</CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to="/growth/coupons-referrals">Open analytics</Link>
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <GrowthPromoStat
              icon={<Ticket className="h-4 w-4" />}
              label="Coupon revenue"
              value={fmt.npr(coupons.data?.totals.gross_revenue ?? 0)}
              hint={`${fmt.number(coupons.data?.totals.redemptions ?? 0)} redemptions`}
              loading={coupons.isLoading}
              error={coupons.isError}
            />
            <GrowthPromoStat
              icon={<Sparkles className="h-4 w-4" />}
              label="Coupon CAC"
              value={fmt.npr(coupons.data?.totals.incentive_cac ?? null)}
              hint="Discount-only CAC"
              loading={coupons.isLoading}
              error={coupons.isError}
            />
            <GrowthPromoStat
              icon={<Users className="h-4 w-4" />}
              label="Referred users"
              value={fmt.number(referrals.data?.totals.referred_users ?? 0)}
              hint={`${fmt.pct(referrals.data?.totals.conversion_rate ?? 0)} converted`}
              loading={referrals.isLoading}
              error={referrals.isError}
            />
            <GrowthPromoStat
              icon={<Sparkles className="h-4 w-4" />}
              label="Referral CAC"
              value={fmt.npr(referrals.data?.totals.incentive_cac ?? null)}
              hint="Estimated free-delivery cost"
              loading={referrals.isLoading}
              error={referrals.isError}
            />
          </div>
        </CardBody>
      </Card>
    </>
  );
}

function GrowthPromoStat({
  icon,
  label,
  value,
  hint,
  loading,
  error,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  hint: string;
  loading?: boolean;
  error?: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-surface px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span className="text-muted-foreground">{icon}</span>
      </div>
      {loading ? (
        <Skeleton className="mt-3 h-7 w-24" />
      ) : (
        <div
          className={cn(
            'mt-2 text-xl font-semibold',
            error ? 'text-muted-foreground' : 'kpi-num',
          )}
        >
          {error ? '—' : value}
        </div>
      )}
      <div className={cn('mt-1 text-xs', error ? 'text-danger' : 'text-muted-foreground')}>
        {error ? 'Could not load promotions' : hint}
      </div>
    </div>
  );
}

interface FunnelRowProps {
  label: string;
  cur: number;
  prev: number | undefined;
  ratio: number;
  compareEnabled: boolean;
  index: number;
}

function FunnelRow({ label, cur, prev, ratio, compareEnabled, index }: FunnelRowProps) {
  const delta = prev != null && prev > 0 ? (cur - prev) / prev : null;
  const Icon = delta == null ? Minus : delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  const tone = delta == null ? 'text-muted-foreground' : delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-muted-foreground';
  // Cap visual ratio so tiny rows still get a visible nub.
  const widthPct = Math.max(2, Math.round(Math.min(1, ratio) * 100));

  return (
    <li className="rounded-md border border-border bg-surface px-3 py-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-muted-foreground">{label}</span>
        <div className="flex items-center gap-2">
          <span className="kpi-num font-semibold">
            <AnimatedNumber value={cur} format={(n) => fmt.number(n)} duration={450} />
          </span>
          {compareEnabled ? (
            <span className={cn('inline-flex items-center gap-0.5 text-[11px] font-medium', tone)}>
              <Icon className="h-3 w-3" aria-hidden />
              {delta != null ? fmt.delta(delta, { unit: 'pct' }) : '—'}
            </span>
          ) : null}
        </div>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-500',
            // Slight tone descent down the funnel so the eye reads top→bottom.
            index === 0 && 'bg-primary',
            index === 1 && 'bg-primary/85',
            index === 2 && 'bg-primary/70',
            index === 3 && 'bg-primary/55',
          )}
          style={{ width: `${widthPct}%`, transitionTimingFunction: 'var(--ease-out)' }}
        />
      </div>
    </li>
  );
}

function RetentionLegend() {
  return (
    <div className="hidden items-center gap-1.5 text-[10px] text-muted-foreground sm:flex">
      <span>0%</span>
      <span className="inline-flex h-2 w-24 overflow-hidden rounded-sm">
        <span style={{ background: 'hsl(var(--primary) / 0.05)' }} className="h-full flex-1" />
        <span style={{ background: 'hsl(var(--primary) / 0.20)' }} className="h-full flex-1" />
        <span style={{ background: 'hsl(var(--primary) / 0.40)' }} className="h-full flex-1" />
        <span style={{ background: 'hsl(var(--primary) / 0.60)' }} className="h-full flex-1" />
        <span style={{ background: 'hsl(var(--primary) / 0.85)' }} className="h-full flex-1" />
      </span>
      <span>100%</span>
    </div>
  );
}

function CohortMatrix({
  cohorts,
}: {
  cohorts: { cohort: string; size: number; retention: { week: number; users: number; rate: number }[] }[];
}) {
  const maxWeeks = cohorts.reduce((m, c) => Math.max(m, c.retention.length), 0);

  return (
    <div className="overflow-x-auto pt-4">
      <table className="w-full min-w-[640px] text-xs">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="px-3 py-2 font-medium">Cohort</th>
            <th className="px-3 py-2 text-right font-medium">Size</th>
            {Array.from({ length: maxWeeks }).map((_, w) => (
              <th key={w} className="px-2 py-2 text-right font-medium">
                W{w}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((c) => (
            <tr key={c.cohort} className="border-t border-border">
              <td className="px-3 py-2 font-medium">{fmt.date(c.cohort, 'd MMM')}</td>
              <td className="px-3 py-2 text-right kpi-num text-muted-foreground">
                {fmt.number(c.size)}
              </td>
              {Array.from({ length: maxWeeks }).map((_, w) => {
                const cell = c.retention[w];
                return (
                  <td key={w} className="px-1 py-1">
                    <RetentionCell rate={cell?.rate ?? 0} users={cell?.users ?? 0} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RetentionCell({ rate, users }: { rate: number; users: number }) {
  const opacity = Math.min(0.95, Math.max(0.05, rate));
  return (
    <div
      className={cn(
        'flex h-9 items-center justify-center rounded text-[11px] font-medium tabular-nums',
        rate > 0.4 ? 'text-primary-foreground' : 'text-foreground',
      )}
      style={{
        background: rate > 0 ? `hsl(var(--primary) / ${opacity.toFixed(2)})` : 'transparent',
      }}
      title={`${users} users · ${(rate * 100).toFixed(1)}%`}
    >
      {rate > 0 ? `${(rate * 100).toFixed(0)}%` : '—'}
    </div>
  );
}
